import type { CorpusAnalysisResult } from '../hooks/useCorpusAnalysis'
import type { Witness } from '../store/booksStore'

export const ALL_CLUSTERS_SCOPE_ID = 'all'

export interface ManualStudioScopeOption {
  clusterId: string
  members: string[]
  groundTruthSigla: string
  size: number
  label: string
  compactLabel: string
  isAggregate: boolean
}

const buildScopedOption = (
  clusterId: string,
  members: string[],
  groundTruthSigla: string,
  size: number
): ManualStudioScopeOption => ({
  clusterId,
  members,
  groundTruthSigla,
  size,
  label: `Cluster ${clusterId}`,
  compactLabel: `C${clusterId}`,
  isAggregate: false
})

export const buildManualStudioScopeOptions = (
  analysisData: CorpusAnalysisResult | null,
  witnesses: Witness[]
): ManualStudioScopeOption[] => {
  const orderedWitnessSiglas = witnesses
    .map(witness => witness.sigla)
    .filter(sigla => typeof sigla === 'string' && sigla.trim().length > 0)

  if (!analysisData) {
    if (orderedWitnessSiglas.length === 0) {
      return []
    }
    return [
      {
        clusterId: ALL_CLUSTERS_SCOPE_ID,
        members: orderedWitnessSiglas,
        groundTruthSigla: orderedWitnessSiglas[0],
        size: orderedWitnessSiglas.length,
        label: 'All Witnesses',
        compactLabel: 'ALL',
        isAggregate: true
      }
    ]
  }

  let baseOptions: ManualStudioScopeOption[] = []

  if (analysisData.progressive_plan?.clusters?.length) {
    baseOptions = analysisData.progressive_plan.clusters
      .map(cluster => {
        const fallbackMembers = analysisData.clusters?.[cluster.cluster_id] || []
        const orderedMembers = cluster.order?.length ? cluster.order : fallbackMembers
        const members = orderedMembers.filter(Boolean)
        return buildScopedOption(
          cluster.cluster_id,
          members,
          cluster.ground_truth || members[0] || '',
          cluster.size || members.length
        )
      })
      .filter(cluster => cluster.members.length > 0)
  } else {
    baseOptions = Object.entries(analysisData.clusters || {})
      .sort(([left], [right]) => {
        const leftNumeric = Number.parseInt(left, 10)
        const rightNumeric = Number.parseInt(right, 10)
        if (Number.isNaN(leftNumeric) || Number.isNaN(rightNumeric)) {
          return left.localeCompare(right)
        }
        return leftNumeric - rightNumeric
      })
      .map(([clusterId, members]) => buildScopedOption(
        clusterId,
        members.filter(Boolean),
        members[0] || '',
        members.length
      ))
      .filter(cluster => cluster.members.length > 0)
  }

  if (baseOptions.length <= 1) {
    return baseOptions
  }

  const orderedMembers: string[] = []
  const seenMembers = new Set<string>()
  const pushMember = (sigla: string) => {
    const normalized = typeof sigla === 'string' ? sigla.trim() : ''
    if (!normalized || seenMembers.has(normalized)) {
      return
    }
    seenMembers.add(normalized)
    orderedMembers.push(normalized)
  }

  ;(analysisData.progressive_plan?.full_order || []).forEach(pushMember)
  baseOptions.forEach(cluster => cluster.members.forEach(pushMember))
  ;(analysisData.labels || []).forEach(pushMember)
  orderedWitnessSiglas.forEach(pushMember)

  if (orderedMembers.length === 0) {
    return baseOptions
  }

  const recommendedGroundTruth = typeof analysisData.recommended_ground_truth === 'string'
    ? analysisData.recommended_ground_truth.trim()
    : ''
  const fallbackAggregateGroundTruth =
    baseOptions.find(option => orderedMembers.includes(option.groundTruthSigla))?.groundTruthSigla || orderedMembers[0]
  const aggregateGroundTruth = orderedMembers.includes(recommendedGroundTruth)
    ? recommendedGroundTruth
    : fallbackAggregateGroundTruth

  return [
    {
      clusterId: ALL_CLUSTERS_SCOPE_ID,
      members: orderedMembers,
      groundTruthSigla: aggregateGroundTruth,
      size: orderedMembers.length,
      label: 'All Clusters',
      compactLabel: 'ALL',
      isAggregate: true
    },
    ...baseOptions
  ]
}

export const describeManualStudioScope = (
  scope: ManualStudioScopeOption | null | undefined
): string => {
  if (!scope) {
    return 'the current scope'
  }
  return scope.isAggregate ? 'all clusters' : `cluster ${scope.clusterId}`
}
